import { __extends } from "tslib";
import { TextureProjectorEvent } from '../events/TextureProjectorEvent';
import { MethodBase, _Shader_MethodBase } from './MethodBase';
/**
 * ProjectiveTextureMethod is a material method used to project a texture unto the surface of an object.
 * This can be used for various effects apart from acting like a normal projector, such as projecting fake shadows
 * unto a surface, the impact of light coming through a stained glass window, ...
 */
var EffectProjectiveTextureMethod = /** @class */ (function (_super) {
    __extends(EffectProjectiveTextureMethod, _super);
    /**
     * Creates a new ProjectiveTextureMethod object.
     *
     * @param projector The TextureProjector object that defines the projection properties as well as the texture.
     * @param mode The blend mode with which the texture is blended unto the surface.
     *
     * @see away3d.entities.TextureProjector
     */
    function EffectProjectiveTextureMethod(projector, mode, exposure) {
        if (mode === void 0) { mode = 'multiply'; }
        if (exposure === void 0) { exposure = 1; }
        var _this = _super.call(this) || this;
        _this._onTextureChangedDelegate = function (event) { return _this._onTextureChanged(event); };
        _this._projector = projector;
        _this._exposure = exposure;
        _this._mode = mode;
        _this._projector.addEventListener(TextureProjectorEvent.TEXTURE_CHANGE, _this._onTextureChangedDelegate);
        return _this;
    }
    Object.defineProperty(EffectProjectiveTextureMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return EffectProjectiveTextureMethod.assetType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectProjectiveTextureMethod.prototype, "exposure", {
        /**
         *
         */
        get: function () {
            return this._exposure;
        },
        set: function (value) {
            if (this._exposure == value)
                return;
            this._exposure = value;
            this.invalidate();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectProjectiveTextureMethod.prototype, "mode", {
        /**
         * The blend mode with which the texture is blended unto the object.
         * ProjectiveTextureMethod.MULTIPLY can be used to project shadows. To prevent clamping, the texture's alpha should be white!
         * ProjectiveTextureMethod.ADD can be used to project light, such as a slide projector or light coming through stained glass. To prevent clamping, the texture's alpha should be black!
         * ProjectiveTextureMethod.MIX provides normal alpha blending. To prevent clamping, the texture's alpha should be transparent!
         */
        get: function () {
            return this._mode;
        },
        set: function (value) {
            if (this._mode == value)
                return;
            this._mode = value;
            this.invalidateShaderProgram();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(EffectProjectiveTextureMethod.prototype, "projector", {
        /**
         * The TextureProjector object that defines the projection properties as well as the texture.
         *
         * @see away3d.entities.TextureProjector
         */
        get: function () {
            return this._projector;
        },
        set: function (value) {
            if (this._projector == value)
                return;
            if (this._projector)
                this._projector.removeEventListener(TextureProjectorEvent.TEXTURE_CHANGE, this._onTextureChangedDelegate);
            this._projector = value;
            if (this._projector)
                this._projector.addEventListener(TextureProjectorEvent.TEXTURE_CHANGE, this._onTextureChangedDelegate);
            this.updateTexture();
        },
        enumerable: false,
        configurable: true
    });
    EffectProjectiveTextureMethod.prototype._onTextureChanged = function (event) {
        this.updateTexture();
    };
    EffectProjectiveTextureMethod.prototype.updateTexture = function () {
        if (this._texture)
            this.iRemoveTexture(this._texture);
        this._texture = (this._projector) ? this._projector.texture : null;
        if (this._texture)
            this.iAddTexture(this._texture);
        this.invalidateShaderProgram();
    };
    EffectProjectiveTextureMethod.OVERLAY = 'overlay';
    EffectProjectiveTextureMethod.MULTIPLY = 'multiply';
    EffectProjectiveTextureMethod.ADD = 'add';
    EffectProjectiveTextureMethod.MIX = 'mix';
    EffectProjectiveTextureMethod.assetType = '[asset EffectProjectiveTextureMethod]';
    return EffectProjectiveTextureMethod;
}(MethodBase));
export { EffectProjectiveTextureMethod };
import { ErrorBase, Matrix3D } from '@awayjs/core';
import { ShaderBase } from '@awayjs/renderer';
/**
 * ProjectiveTextureMethod is a material method used to project a texture unto the surface of an object.
 * This can be used for various effects apart from acting like a normal projector, such as projecting fake shadows
 * unto a surface, the impact of light coming through a stained glass window, ...
 */
var _Shader_EffectProjectiveTextureMethod = /** @class */ (function (_super) {
    __extends(_Shader_EffectProjectiveTextureMethod, _super);
    /**
     * Creates a new EffectEnvMapChunk.
     */
    function _Shader_EffectProjectiveTextureMethod(method, shader) {
        var _this = _super.call(this, method, shader) || this;
        _this._method = method;
        _this._shader = shader;
        return _this;
    }
    _Shader_EffectProjectiveTextureMethod.prototype._initVO = function (chunkVO) {
        this._texture = this._method.projector.texture.getAbstraction(this._shader);
        this._texture._initVO(chunkVO);
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectProjectiveTextureMethod.prototype._initConstants = function () {
        this._texture._initConstants();
        var index = this._exposureIndex;
        var data = this._shader.fragmentConstantData;
        data[index] = this._method.exposure;
        data[index + 1] = 0.5;
        data[index + 2] = 4;
        data[index + 3] = -1;
        this._projectionMatrix = new Matrix3D(new Float32Array(this._shader.vertexConstantData.buffer, this._projectionIndex * 4, 16));
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectProjectiveTextureMethod.prototype._cleanCompilationData = function () {
        _super.prototype._cleanCompilationData.call(this);
        this._uvVarying = null;
        this._projectionMatrix = null;
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectProjectiveTextureMethod.prototype._getVertexCode = function (registerCache, sharedRegisters) {
        var code = '';
        var projReg = registerCache.getFreeVertexConstant();
        registerCache.getFreeVertexConstant();
        registerCache.getFreeVertexConstant();
        registerCache.getFreeVertexConstant();
        this._projectionIndex = projReg.index * 4;
        this._uvVarying = registerCache.getFreeVarying();
        code += 'm44 ' + this._uvVarying + ', ' + sharedRegisters.animatedPosition + ', ' + projReg + '\n';
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectProjectiveTextureMethod.prototype._getFragmentCode = function (targetReg, registerCache, sharedRegisters) {
        var code = '';
        var col = registerCache.getFreeFragmentVectorTemp();
        registerCache.addFragmentTempUsages(col, 1);
        var temp = registerCache.getFreeFragmentVectorTemp();
        //var toTexReg:ShaderRegisterElement = registerCache.getFreeFragmentConstant();
        //methodVO.fragmentConstantsIndex = toTexReg.index*4;
        var exposure = registerCache.getFreeFragmentConstant();
        this._exposureIndex = exposure.index * 4;
        // code += "mul " + col + ".xy, " + this._uvVarying + ".xy, " + toTexReg + ".xy	\n" +
        // 	"add " + col + ".xy, " + col + ".xy, " + toTexReg + ".xx	\n";
        code += this._texture._getFragmentCode(col, registerCache, sharedRegisters, this._uvVarying);
        code += 'mul ' + col + ', ' + col + ', ' + exposure + '.xxx\n' +
            'add ' + col + ', ' + col + ', ' + exposure + '.xxx\n';
        if (this._method.mode == EffectProjectiveTextureMethod.MULTIPLY)
            code += 'mul ' + targetReg + '.xyz, ' + targetReg + '.xyz, ' + col + '.xyz			\n';
        else if (this._method.mode == EffectProjectiveTextureMethod.ADD)
            code += 'add ' + targetReg + '.xyz, ' + targetReg + '.xyz, ' + col + '.xyz			\n';
        else if (this._method.mode == EffectProjectiveTextureMethod.MIX) {
            code += 'sub ' + col + '.xyz, ' + col + '.xyz, ' + targetReg + '.xyz				\n' +
                'mul ' + col + '.xyz, ' + col + '.xyz, ' + col + '.w						\n' +
                'add ' + targetReg + '.xyz, ' + targetReg + '.xyz, ' + col + '.xyz			\n';
        }
        else if (this._method.mode == EffectProjectiveTextureMethod.OVERLAY) {
            code += 'sge ' + temp + ', ' + targetReg + ', ' + exposure + '.yyy\n'; // temp = (base >= 0.5)? 1 : 0
            code += 'sub ' + targetReg + ', ' + targetReg + ', ' + temp + '\n'; // base = temp? (base - 1 : base)
            code += 'sub ' + col + ', ' + col + ', ' + temp + '\n'; // blend = temp? (blend - 1 : blend)
            code += 'mul ' + col + ', ' + col + ', ' + targetReg + '\n'; // blend = blend * base
            code += 'sub ' + targetReg + ', ' + exposure + '.yyy, ' + temp + '\n'; // base = temp? -0.5 : 0.5
            code += 'mul ' + targetReg + ', ' + exposure + '.zzz, ' + targetReg + '\n'; // base = temp? -2 : 2
            code += 'mul ' + col + ', ' + col + ', ' + targetReg + '\n'; // blend = blend * ( -2 : 2)
            code += 'add ' + targetReg + ', ' + col + ', ' + temp + '\n'; //blend = temp? (blend + 1 : blend)
        }
        else
            throw new ErrorBase('Unknown mode "' + this._method.mode + '"');
        registerCache.removeFragmentTempUsage(col);
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectProjectiveTextureMethod.prototype._setRenderState = function (renderState) {
        var matrix3D = Matrix3D.CALCULATION_MATRIX;
        matrix3D.copyFrom(this._method.projector.projection.viewMatrix3D);
        matrix3D.prepend(renderState.renderSceneTransform);
        this._projectionMatrix.copyFrom(matrix3D, true);
        this._texture._setRenderState(renderState);
    };
    /**
     * @inheritDoc
     */
    _Shader_EffectProjectiveTextureMethod.prototype._activate = function () {
        this._texture.activate();
        if (this._invalid)
            this._shader.fragmentConstantData[this._exposureIndex] = this._method.exposure;
    };
    return _Shader_EffectProjectiveTextureMethod;
}(_Shader_MethodBase));
export { _Shader_EffectProjectiveTextureMethod };
ShaderBase.registerAbstraction(_Shader_EffectProjectiveTextureMethod, EffectProjectiveTextureMethod);
